import {
  Button,
  Chip,
  Dialog,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import React, { useEffect, useState } from 'react';

import { IBrandSafetyRule } from '~/src/dash/api/brand-safety-rules';
import { IUser } from '~/src/dash/api/console-api';
import {
  ICreativeValidation,
  REVIEW_STATUS_ALLOWED,
  REVIEW_STATUS_BLOCKED,
  REVIEW_STATUS_REVIEWED,
  REVIEW_STATUS_UNREVIEWED,
  updateCreativeValidation,
} from '~/src/dash/api/creative-validation';
import {
  AllowIconHuge,
  BlockIconHuge,
  CheckmarkIconHuge,
  UndoIconHuge,
} from '~/src/dash/components/icons/IconV2/custom';
import { ICreativeValidationRowData } from '~/src/dash/components/list-table';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import {
  ButtonV2,
  Col,
  Cols,
  fontSizing,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  Textarea,
  TextButton,
} from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { generateBSRMenuForCreativeValidationRow } from '~/src/dash/utils/brand-safety-rules';
import { getAllIABCategories, IAB_CATEGORIES } from '~/src/dash/utils/iab-category';

export interface IEditCreativeValidationModalProps {
  classes: any;
  openEditCreativeReviewModal: boolean;
  handleClose: () => void;
  creativeData: ICreativeValidationRowData;
  handleApplyFilterKeyDown: any;
  handleRefreshCreativeReview: (rowData: ICreativeValidationRowData) => void;
  getCreativeValidations: (
    query?: any,
  ) => Promise<{ data: ICreativeValidationRowData[]; page: number; totalCount: number }>;
  handleCreativeReviewMessage: (error: boolean, message: string) => void;
  updatingReviewStatus: boolean;
  brandSafetyRules: IBrandSafetyRule[];
  user: IUser;
  reviewStatusFilter: number;
}

export default function EditCreativeReviewModal({
  brandSafetyRules,
  classes,
  creativeData,
  getCreativeValidations,
  handleApplyFilterKeyDown,
  handleClose,
  handleCreativeReviewMessage,
  handleRefreshCreativeReview,
  openEditCreativeReviewModal,
  reviewStatusFilter,
  updatingReviewStatus,
  user,
}: IEditCreativeValidationModalProps) {
  const [currentAdomain, setCurrentAdomain] = useState('');
  const [currentBSRMenuList, setCurrentBSRMenuList] = useState({} as IMenuList);
  const [currentIABCategoryPrefix, setCurrentIABCategoryPrefix] = useState('');
  const [currentIABCategorySuffix, setCurrentIABCategorySuffix] = useState('');
  const [currentAllIABCategoryCodes, setCurrentAllIABCategoryCodes] = useState('');
  const [currentCreativeData, setCurrentCreativeData] = useState(creativeData);
  const [currentNotes, setCurrentNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const iabCategoryKey =
      creativeData && creativeData.IABCategoryCodes && creativeData.IABCategoryCodes.length > 0
        ? creativeData.IABCategoryCodes.split(';')[0].split('-')[0]
        : 'IAB';
    setCurrentAdomain(creativeData.Adomain);
    setCurrentIABCategoryPrefix(iabCategoryKey);

    setCurrentIABCategorySuffix('');
    setCurrentAllIABCategoryCodes(creativeData.IABCategoryCodes);
    setCurrentCreativeData(creativeData);

    const newBSRMenuList = generateBSRMenuForCreativeValidationRow(
      user,
      brandSafetyRules,
      creativeData,
    );
    setCurrentBSRMenuList(newBSRMenuList);
    setCurrentNotes(creativeData.Notes);
  }, [creativeData]);

  const handleAddIABCategory = () => {
    setCurrentAllIABCategoryCodes((prev) => {
      const selectedIABs = prev.split(';');
      const newIABCode = `${currentIABCategoryPrefix}${
        currentIABCategorySuffix.length > 0 ? '-' + currentIABCategorySuffix : ''
      }`;
      if (!selectedIABs.includes(newIABCode)) {
        selectedIABs.push(newIABCode);
      }
      return selectedIABs.filter((selected) => selected.length > 0).join(';');
    });
  };

  const handleClearIABCategory = () => {
    setCurrentAllIABCategoryCodes(() => {
      return '';
    });
  };

  const handleDeleteFromSelectedIABCategories = (index: number) => {
    setCurrentAllIABCategoryCodes((prev) => {
      const updatedSelectedIABCatCodes = prev.split(';').filter((_, i) => i !== index);
      return updatedSelectedIABCatCodes.join(';');
    });
  };

  // update creative validation through actions
  const handleSaveActionEditedCreativeReview = (newReviewStatus: number) => {
    setIsUpdating(true);

    const selectedBrandSafetyRuleIDs: number[] = Object.values(currentBSRMenuList)
      .filter((bsrValue) => bsrValue.selected)
      .map((bsrValue) => parseInt(bsrValue.ID, 10))
      .filter((bsrValue) => !Number.isNaN(bsrValue));

    updateCreativeValidation(creativeData.PublisherID.toString(), {
      OriginalCreativeURL: creativeData.OriginalCreativeURL,
      CreativeURL: creativeData.CreativeURL,
      Adomain: creativeData.Adomain,
      IABCategory: creativeData.IABCategoryCodes,
      ReviewStatus:
        creativeData.ReviewStatus !== newReviewStatus ? newReviewStatus : REVIEW_STATUS_UNREVIEWED,
      Notes: currentNotes,
      AdomainOverride: currentAdomain,
      IABCategoryOverride: currentAllIABCategoryCodes,
      BrandSafetyRuleIDs: selectedBrandSafetyRuleIDs,
    } as Partial<ICreativeValidation>)
      .then(() => {
        getCreativeValidations({ page: 0 })
          .then(() => {
            handleRefreshCreativeReview(creativeData);
          })
          .catch(() => {
            handleCreativeReviewMessage(
              true,
              'Error while getting the list of creative_validations from the Snowflake.',
            );
          });
      })
      .then(() => {
        handleCreativeReviewMessage(
          false,
          'Successfully updated the creative review in Snowflake.',
        );
        handleClose();
        setIsUpdating(false);
      })
      .catch(() => {
        handleCreativeReviewMessage(true, 'Error while updating the creative review in Snowflake.');
      });
  };

  // update creative validation through save button
  const handleSaveEditedCreativeReview = () => {
    setIsUpdating(true);

    const selectedBrandSafetyRuleIDs: number[] = Object.values(currentBSRMenuList)
      .filter((bsrValue) => bsrValue.selected)
      .map((bsrValue) => parseInt(bsrValue.ID, 10))
      .filter((bsrValue) => !Number.isNaN(bsrValue));

    updateCreativeValidation(creativeData.PublisherID.toString(), {
      OriginalCreativeURL: creativeData.OriginalCreativeURL,
      CreativeURL: creativeData.CreativeURL,
      Adomain: creativeData.Adomain,
      IABCategory: creativeData.IABCategoryCodes,
      ReviewStatus: creativeData.ReviewStatus,
      Notes: currentNotes,
      AdomainOverride: currentAdomain,
      IABCategoryOverride: currentAllIABCategoryCodes,
      BrandSafetyRuleIDs: selectedBrandSafetyRuleIDs,
    } as Partial<ICreativeValidation>)
      .then(() => {
        getCreativeValidations({ page: 0 })
          .then(() => {
            handleRefreshCreativeReview(creativeData);
          })
          .catch(() => {
            handleCreativeReviewMessage(
              true,
              'Error while getting the list of creative_validations from the Snowflake.',
            );
          });
      })
      .then(() => {
        handleCreativeReviewMessage(
          false,
          'Successfully updated the creative review in Snowflake.',
        );
        handleClose();
        setIsUpdating(false);
      })
      .catch(() => {
        handleCreativeReviewMessage(true, 'Error while updating the creative review in Snowflake.');
      });
  };

  const renderCreativeURLActionInEditModal = (rowData: ICreativeValidationRowData) => {
    const numBSRSelected = Object.values(currentBSRMenuList).filter(
      (bsrValue) => bsrValue.selected,
    ).length;
    return (
      <Cols
        wrap
        style={{
          alignContent: 'center',
          gap: spacing.extraLarge,
        }}
      >
        <Col>
          <Rows>
            <Row
              style={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
              noPadding
            >
              <Tooltip
                title={
                  <Grid>
                    <Typography className={classes.tooltipBold}>
                      {rowData.ReviewStatus === REVIEW_STATUS_REVIEWED
                        ? 'Unreview'
                        : 'Mark As Reviewed'}
                    </Typography>
                    <Typography className={classes.tooltipSmall}>
                      {rowData.ReviewStatus === REVIEW_STATUS_REVIEWED
                        ? 'Mark as unreviewed'
                        : numBSRSelected > 0
                        ? 'Action not available for brand safety bypass'
                        : 'Ad is ok to run under current rules'}
                    </Typography>
                  </Grid>
                }
              >
                <span>
                  <ModelSecondaryButton
                    disabled={isUpdating || numBSRSelected > 0}
                    onClick={() => {
                      if (!updatingReviewStatus) {
                        handleSaveActionEditedCreativeReview(REVIEW_STATUS_REVIEWED);
                      }
                    }}
                  >
                    {/* Reviewed check box */}
                    {rowData.ReviewStatus === REVIEW_STATUS_REVIEWED ? (
                      <UndoIconHuge />
                    ) : (
                      <CheckmarkIconHuge />
                    )}
                  </ModelSecondaryButton>
                </span>
              </Tooltip>
            </Row>
            <Row style={{ textAlign: 'center' }} noPadding>
              <Text small bold>
                {rowData.ReviewStatus === REVIEW_STATUS_REVIEWED ? 'Unreview' : 'Review'}
              </Text>
            </Row>
          </Rows>
        </Col>
        <Col>
          <Rows>
            <Row style={{ textAlign: 'center' }} noPadding>
              <Tooltip
                title={
                  <Grid>
                    <Typography className={classes.tooltipBold}>
                      {rowData.ReviewStatus === REVIEW_STATUS_ALLOWED ? 'Unreview' : 'Allow'}
                    </Typography>
                    <Typography className={classes.tooltipSmall}>
                      {rowData.ReviewStatus === REVIEW_STATUS_ALLOWED
                        ? 'Mark as unreviewed'
                        : 'Exception to bypass all brand safety rules'}
                    </Typography>
                  </Grid>
                }
              >
                <ModelSecondaryButton
                  disabled={isUpdating}
                  onClick={() => {
                    if (!updatingReviewStatus) {
                      handleSaveActionEditedCreativeReview(REVIEW_STATUS_ALLOWED);
                    }
                  }}
                >
                  {/* Allowed Unlocked Icon */}
                  {rowData.ReviewStatus === REVIEW_STATUS_ALLOWED ? (
                    <UndoIconHuge />
                  ) : (
                    <AllowIconHuge />
                  )}
                </ModelSecondaryButton>
              </Tooltip>
            </Row>
            <Row style={{ textAlign: 'center' }} noPadding>
              <Text small bold>
                {rowData.ReviewStatus === REVIEW_STATUS_ALLOWED ? 'Unreview' : 'Allow'}
              </Text>
            </Row>
          </Rows>
        </Col>
        <Col>
          <Rows>
            <Row
              style={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
              noPadding
            >
              <Tooltip
                title={
                  <Grid>
                    <Typography className={classes.tooltipBold}>
                      {rowData.ReviewStatus === REVIEW_STATUS_BLOCKED ? 'Unreview' : 'Block'}
                    </Typography>
                    <Typography className={classes.tooltipSmall}>
                      {rowData.ReviewStatus === REVIEW_STATUS_BLOCKED
                        ? 'Mark as unreviewed'
                        : numBSRSelected > 0
                        ? 'Action not available for brand safety bypass'
                        : 'Prevent from serving altogether'}
                    </Typography>
                  </Grid>
                }
              >
                <span>
                  <ModelSecondaryButton
                    disabled={isUpdating || numBSRSelected > 0}
                    onClick={() => {
                      if (!updatingReviewStatus) {
                        handleSaveActionEditedCreativeReview(REVIEW_STATUS_BLOCKED);
                      }
                    }}
                  >
                    {/* Blocked Circle with Slash */}
                    {rowData.ReviewStatus === REVIEW_STATUS_BLOCKED ? (
                      <UndoIconHuge />
                    ) : (
                      <BlockIconHuge />
                    )}
                  </ModelSecondaryButton>
                </span>
              </Tooltip>
            </Row>
            <Row style={{ textAlign: 'center' }} noPadding>
              <Text small bold>
                {rowData.ReviewStatus === REVIEW_STATUS_BLOCKED ? 'Unreview' : 'Block'}
              </Text>
            </Row>
          </Rows>
        </Col>
      </Cols>
    );
  };

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={openEditCreativeReviewModal}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
    >
      <Rows largeVerticalPadding extraLargeHorizontalPadding>
        <Row
          noPadding
          marginTop={spacing.small}
          style={{
            textAlign: 'right',
          }}
        >
          {creativeData && creativeData.ReviewStatus === REVIEW_STATUS_UNREVIEWED && (
            <Button
              style={{
                minWidth: '0px',
                padding: '0px',
              }}
              onClick={handleClose}
            >
              <img src={'/assets/shared/svg/Close.svg'} width='24px' height='24px' />
            </Button>
          )}
        </Row>
        <Row>
          <Text header>Edit Creative</Text>
          <Text>
            Edit Adomain and IAB Category.&nbsp;
            <Span bold>Note that changes will apply to all instances of this mediafile.</Span>
          </Text>
        </Row>
        {/* Creative URL */}
        <Row>
          <Cols style={{ alignItems: 'center' }}>
            <Col style={{ minWidth: '240px' }}>
              <Text heavy>Creative URL</Text>
            </Col>
            <Col
              width={380}
              style={{
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  border: '1px solid rgba(0, 0, 0, 0.26)',
                  borderRadius: '4px',
                  color: 'rgba(0, 0, 0, 0.5)',
                  width: '100%',
                  padding: '6px 16px',
                }}
              >
                <Typography noWrap>{creativeData.CreativeURL}</Typography>
              </div>
            </Col>
          </Cols>
        </Row>
        {/* Adomain */}
        <Row smallVerticalPadding>
          <Cols style={{ alignItems: 'flex-start' }}>
            <Col style={{ minWidth: '240px' }}>
              <Text heavy>Adomain</Text>
            </Col>
            <Col style={{ minWidth: '240px' }}>
              <TextField
                style={{
                  display: 'flex',
                  paddingBottom: '20px',
                  width: '100%',
                  textOverflow: 'ellipsis',
                }}
                label='enter adomain'
                name='Adomain'
                value={currentAdomain}
                onChange={(event) => setCurrentAdomain(event.target.value)}
              />
            </Col>
          </Cols>
        </Row>
        <Row smallVerticalPadding>
          <Cols style={{ alignItems: 'flex-start' }}>
            <Col style={{ minWidth: '240px' }}>
              <Text heavy>IAB Category</Text>
            </Col>
            <Cols wrap gap='4px' style={{ width: '100%' }}>
              {/* IAB Category Prefix */}
              <Col style={{ minWidth: '240px' }}>
                <TextField
                  style={{
                    paddingRight: '16px',
                    paddingBottom: '20px',
                    width: '100%',
                  }}
                  select
                  name='IABCategoryPrefix'
                  className='pb-campaign-form-view_iab_cat'
                  label='Select category'
                  value={currentIABCategoryPrefix}
                  onChange={(e) => setCurrentIABCategoryPrefix(e.target.value)}
                  onKeyDown={handleApplyFilterKeyDown}
                >
                  {getAllIABCategories().map((val) => {
                    return (
                      <MenuItem value={val}>
                        {val}: {IAB_CATEGORIES[val].label}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Col>
              {/* IAB Category Suffix */}
              <Col
                width={!['', 'IAB'].includes(currentIABCategoryPrefix) ? 200 : 0}
                style={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {!['', 'IAB'].includes(currentIABCategoryPrefix) && (
                  <TextField
                    style={{
                      paddingRight: '20px',
                      paddingBottom: '20px',
                      minWidth: '240px',
                    }}
                    select
                    name='IABCategorySuffix'
                    className='pb-campaign-form-view_iab_cat'
                    label='Sub category'
                    value={currentIABCategorySuffix}
                    onChange={(e) => setCurrentIABCategorySuffix(e.target.value)}
                    onKeyDown={handleApplyFilterKeyDown}
                  >
                    {Object.keys(
                      (IAB_CATEGORIES[currentIABCategoryPrefix] || { values: [] }).values || {},
                    ).map((val) => {
                      return (
                        <MenuItem value={val}>
                          {val}: {IAB_CATEGORIES[currentIABCategoryPrefix].values[val]}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                )}
              </Col>
              {/* Add button */}
              <Col style={{ width: '136px' }}>
                <Cols
                  style={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <Col
                    largeVerticalPadding
                    style={{
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <TextButton
                      style={{ display: 'flex', alignItems: 'center' }}
                      disabled={['', 'IAB'].includes(currentIABCategoryPrefix)}
                      onClick={handleAddIABCategory}
                    >
                      + ADD
                    </TextButton>
                  </Col>
                  <Col
                    largeVerticalPadding
                    style={{
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <TextButton
                      style={{ display: 'flex', alignItems: 'center' }}
                      disabled={['', 'IAB'].includes(currentIABCategoryPrefix)}
                      onClick={handleClearIABCategory}
                    >
                      CLEAR
                    </TextButton>
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Cols>
        </Row>
        {/* selected iab category codes */}
        <Row smallVerticalPadding>
          <Col
            style={{
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: '240px',
                height: '40px',
                border: '1px solid rgba(0, 0, 0, 0.26)',
                borderRadius: '4px',
                paddingLeft: '4px',
                gap: '4px',
              }}
            >
              {currentAllIABCategoryCodes &&
                currentAllIABCategoryCodes.length > 0 &&
                currentAllIABCategoryCodes
                  .split(';')
                  .filter((code) => code.length > 0)
                  .map((IABCode, index) => {
                    return (
                      <Chip
                        key={index}
                        label={IABCode}
                        onDelete={() => handleDeleteFromSelectedIABCategories(index)}
                      />
                    );
                  })}
            </div>
          </Col>
        </Row>
        {/* Notes Section */}
        <Row>
          <Cols>
            <Col style={{ minWidth: '240px' }}>
              <Text heavy>Notes</Text>
            </Col>
            <Col width={460}>
              <Textarea
                fullWidth
                msDelay={800}
                initialValue={currentNotes}
                handleOnChange={(text: string) => setCurrentNotes(text)}
                minHeight={54}
              />
            </Col>
          </Cols>
        </Row>
        <Row>
          <Cols>
            <Col style={{ minWidth: '240px' }}>
              <Text heavy>
                Actions
                {creativeData.ReviewStatus === REVIEW_STATUS_UNREVIEWED && (
                  <Tooltip
                    title='Save changes and move creative to Reviewed, Allowed, or Blocked'
                    placement='bottom-start'
                  >
                    <InfoIcon
                      style={{
                        verticalAlign: 'bottom',
                        width: '1rem',
                        color: 'rgba(0, 0, 0, 0.26)',
                        paddingBottom: '10px',
                        marginLeft: '4px',
                      }}
                    />
                  </Tooltip>
                )}
              </Text>
            </Col>
            <Col>
              {(reviewStatusFilter === REVIEW_STATUS_UNREVIEWED ||
                reviewStatusFilter === REVIEW_STATUS_ALLOWED) && (
                <Cols>
                  <Text bold>Apply to</Text>
                  <Col paddingLeft={spacing.medium} paddingBottom={spacing.large}>
                    <MaterialSearchDropdownMulti
                      width='300px'
                      fontSize={fontSizing.base}
                      menuTitle='Brand Safety Rules'
                      list={currentBSRMenuList}
                      onChange={(list: IMenuList) => setCurrentBSRMenuList(list)}
                      disabled={Object.keys(currentBSRMenuList).length === 0}
                      selectLimit={-1}
                      forceOpen={false}
                      onlyShowNumberWhenClosed={true}
                      maxHeight={150}
                      enableFixed={true}
                      selectAll={false}
                      showPrefixID
                      menuTitleOverride={
                        Object.keys(currentBSRMenuList).length === 0 ? 'No Brand Safety Rules' : ''
                      }
                    />
                  </Col>
                  <Tooltip
                    title='The only action that can apply to brand safety rules is "Approve." This will allow the creative to bypass the specific brand safety rule(s) selected.'
                    placement='bottom-start'
                  >
                    <InfoIcon
                      style={{
                        verticalAlign: 'bottom',
                        width: '1rem',
                        color: 'rgba(0, 0, 0, 0.26)',
                        marginLeft: '4px',
                      }}
                    />
                  </Tooltip>
                </Cols>
              )}
              {renderCreativeURLActionInEditModal(currentCreativeData)}
            </Col>
          </Cols>
        </Row>
        <Row>
          <Cols
            style={{
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            {/* CANCEL BUTTON*/}
            {creativeData && creativeData.ReviewStatus !== REVIEW_STATUS_UNREVIEWED && (
              <Col paddingRight={spacing.medium}>
                <TextButton disabled={isUpdating} onClick={handleClose}>
                  Cancel
                </TextButton>
              </Col>
            )}
            {/* SAVE BUTTON*/}
            {creativeData && creativeData.ReviewStatus !== REVIEW_STATUS_UNREVIEWED && (
              <Col>
                <ButtonV2 disabled={isUpdating} onClick={handleSaveEditedCreativeReview}>
                  {!isUpdating ? 'Save' : 'Saving'}
                </ButtonV2>
              </Col>
            )}
          </Cols>
        </Row>
      </Rows>
    </Dialog>
  );
}
